/**
 * Simple processing queue to handle content processing sequentially
 * Prevents API rate limits and conflicts when multiple items are uploaded
 */

class ProcessingQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.currentItem = null;
    this.recentlyCompleted = null; // Track recently completed for toast visibility
    this.recentlyCompletedTimeout = null;
  }

  /**
   * Add item to processing queue
   * @param {Object} content - Content item to process
   * @param {Object} options - Processing options
   * @returns {Promise} Promise that resolves when processing is complete
   */
  async addToQueue(content, options = {}) {
    return new Promise((resolve, reject) => {
      // Generate a meaningful display name
      const displayName = this.generateDisplayName(content);
      
      // Check if item is already in queue to prevent duplicates
      const existingItem = this.queue.find(item => item.content.id === content.id);
      if (existingItem) {
        console.log(`⚠️ Item ${displayName} already in queue, skipping duplicate`);
        resolve({ success: false, error: 'Already in queue' });
        return;
      }
      
      this.queue.push({
        content,
        options,
        resolve,
        reject,
        addedAt: Date.now(),
        displayName
      });
      
      console.log(`📋 Added ${displayName} to processing queue. Queue length: ${this.queue.length}`);
      
      // Start processing if not already running
      if (!this.processing) {
        this.processNext();
      }
    });
  }

  /**
   * Generate a meaningful display name for content
   * @param {Object} content - Content item
   * @returns {string} Display name
   */
  generateDisplayName(content) {
    // If we have a title, use it (truncated)
    if (content.title && content.title !== 'Swirl It Content') {
      return content.title.length > 30 
        ? content.title.substring(0, 30) + '...'
        : content.title;
    }
    
    // If we have a channel, use it with a number
    if (content.channel) {
      const channelCount = this.queue.filter(item => 
        item.content.channel === content.channel
      ).length + 1;
      return `${content.channel} Video ${channelCount}`;
    }
    
    // If we have a content type
    if (content.content_type) {
      const typeCount = this.queue.filter(item => 
        item.content.content_type === content.content_type
      ).length + 1;
      return `${content.content_type.charAt(0).toUpperCase() + content.content_type.slice(1)} ${typeCount}`;
    }
    
    // Fallback to generic name
    const genericCount = this.queue.length + 1;
    return `Content ${genericCount}`;
  }

  /**
   * Process the next item in the queue
   */
  async processNext() {
    if (this.queue.length === 0) {
      this.processing = false;
      this.currentItem = null;
      console.log('✅ Processing queue is empty');
      return;
    }

    this.processing = true;
    const queueItem = this.queue.shift();
    this.currentItem = queueItem.displayName;
    const startTime = Date.now();

    console.log(`🔄 Processing queue item: ${queueItem.displayName} (${this.queue.length} remaining)`);
    console.log(`📋 Item details:`, {
      id: queueItem.content.id,
      url: queueItem.content.url,
      channel: queueItem.content.channel,
      status: queueItem.content.progress_status
    });

    // Set a timeout for this specific item (15 minutes max)
    const itemTimeout = setTimeout(() => {
      console.error(`⏰ Item ${queueItem.displayName} timed out after 15 minutes`);
      queueItem.reject(new Error('Processing timeout after 15 minutes'));
      this.processing = false;
      this.currentItem = null;
      this.processNext();
    }, 15 * 60 * 1000);

    try {
      const content = queueItem.content;
      const isYouTube = content.channel === 'YouTube' || content.source_type === 'youtube' || 
                       (content.url && (content.url.includes('youtube.com') || content.url.includes('youtu.be')));
      
      // Check if this is an IMAGE - images are processed by backend worker only
      const isImage = content.source_type === 'image' || content.source_type === 'screenshot' ||
                     content.source_type === 'svg' || content.source_type === 'gif' ||
                     (content.meta?.mimetype?.startsWith('image/'));
      
      // ============================================
      // UNIFIED PROCESSING: ALL content goes to backend worker
      // No more frontend processing - database trigger auto-invokes worker
      // ============================================
      
      const contentType = isImage ? 'image' : (isYouTube ? 'YouTube' : 'video');
      console.log(`🚀 ${queueItem.displayName} is ${contentType} - routing to backend worker`);
      
      // Import supabase dynamically
      const { supabase } = await import('../lib/supabase');
      
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      
      if (!userId) {
        throw new Error('Not authenticated');
      }
      
      // Use RPC to queue the job (bypasses RLS issues)
      // Provider: 'image' for images, 'rapidapi' for YouTube, 'gettranscribe' for other videos
      const provider = isImage ? 'image' : (isYouTube ? 'rapidapi' : 'gettranscribe');
      
      const { data: jobResult, error: jobError } = await supabase
        .rpc('queue_reprocess_job', {
          p_item_id: content.id,
          p_provider: provider
        });
      
      if (jobError) {
        throw new Error(`Failed to queue job: ${jobError.message}`);
      }
      
      console.log(`✅ Job queued via RPC:`, jobResult);
      console.log(`⏳ Backend worker will process (auto-triggered by database)`);
      
      // Worker is auto-triggered by database trigger, but also try manual trigger for immediate processing
      try {
        const token = sessionData?.session?.access_token;
        
        // Worker requires both Authorization header (Supabase) AND x-worker-secret
        const workerResponse = await fetch('https://xlhjevmymaexbhaebkuk.supabase.co/functions/v1/swirl-ingestion-worker', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-worker-secret': 'swirl-worker-secret-2025'
          },
          body: JSON.stringify({})
        });
        
        if (workerResponse.ok) {
          try {
            const json = await workerResponse.json();
            console.log(`✅ Worker triggered - processing started. Response:`, json);
          } catch {
            console.log(`✅ Worker triggered - processing started`);
          }
        } else {
          console.warn(`⚠️ Worker trigger returned ${workerResponse.status}, but job is queued and will process via auto-trigger`);
        }
      } catch (workerError) {
        console.warn(`⚠️ Failed to trigger worker:`, workerError.message);
        console.log(`📋 Job is queued and will be processed by auto-trigger`);
      }
      
      clearTimeout(itemTimeout);
      const duration = Date.now() - startTime;
      console.log(`✅ Queue processing completed for ${queueItem.displayName} in ${(duration / 1000).toFixed(1)}s`);
      
      // Mark as recently completed so toast stays visible
      this.setRecentlyCompleted(queueItem.displayName, contentType);
      
      queueItem.resolve({ 
        success: true, 
        message: `Queued for backend processing (${provider})` 
      });
      
    } catch (error) {
      clearTimeout(itemTimeout);
      const duration = Date.now() - startTime;
      console.error(`❌ Queue processing failed for ${queueItem.displayName} after ${(duration / 1000).toFixed(1)}s:`, error);
      console.error('Error stack:', error.stack);
      
      // Try to mark content as failed
      try {
        const contentService = await import('../services/contentService').then(m => m.default);
        await contentService.updateContent(queueItem.content.id, {
          progress_status: 'failed',
          analysis_status: 'failed',
          progress_percentage: 5 // Mark where it failed
        });
      } catch (updateError) {
        console.error('Failed to update content status on error:', updateError);
      }
      
      queueItem.reject(error);
    } finally {
      clearTimeout(itemTimeout);
      this.currentItem = null;
      
      // Process next item after a short delay to prevent overwhelming APIs
      // Add longer delay if there was an error to prevent rapid failures
      const delay = this.queue.length > 0 ? 3000 : 1000; // 3s if more items, 1s if last
      setTimeout(() => {
        this.processNext();
      }, delay);
    }
  }

  /**
   * Get current queue status
   */
  getStatus() {
    return {
      queueLength: this.queue.length,
      processing: this.processing,
      currentItem: this.currentItem || null,
      queueItems: this.queue.map(item => ({
        id: item.content.id,
        displayName: item.displayName,
        addedAt: item.addedAt,
        waitTime: Date.now() - item.addedAt
      })),
      recentlyCompleted: this.recentlyCompleted // Include recently completed for toast
    };
  }

  /**
   * Mark an item as recently completed (for toast visibility)
   */
  setRecentlyCompleted(displayName, type = 'processing') {
    this.recentlyCompleted = { displayName, type, completedAt: Date.now() };
    
    // Clear after 5 seconds to give user time to see it
    if (this.recentlyCompletedTimeout) {
      clearTimeout(this.recentlyCompletedTimeout);
    }
    this.recentlyCompletedTimeout = setTimeout(() => {
      this.recentlyCompleted = null;
    }, 5000);
  }

  /**
   * Clear the queue (useful for cleanup)
   */
  clearQueue() {
    console.log(`🗑️ Clearing processing queue (${this.queue.length} items)`);
    
    // Reject all pending items
    this.queue.forEach(item => {
      item.reject(new Error('Queue cleared'));
    });
    
    this.queue = [];
    this.processing = false;
    this.currentItem = null;
  }

  /**
   * Check for stuck items and recover them
   * This method should be called periodically to ensure queue health
   */
  checkForStuckItems() {
    const now = Date.now();
    const stuckThreshold = 10 * 60 * 1000; // 10 minutes
    
    // Check if current item is stuck
    if (this.processing && this.currentItem) {
      const currentItem = this.queue.find(item => item.displayName === this.currentItem);
      if (currentItem && (now - currentItem.addedAt) > stuckThreshold) {
        console.warn(`⚠️ Current item ${this.currentItem} appears stuck, forcing recovery`);
        this.forceRecovery();
        return;
      }
    }
    
    // Check for items that have been waiting too long
    const stuckItems = this.queue.filter(item => (now - item.addedAt) > stuckThreshold);
    if (stuckItems.length > 0) {
      console.warn(`⚠️ Found ${stuckItems.length} stuck items in queue, removing them`);
      stuckItems.forEach(item => {
        item.reject(new Error('Item stuck in queue for too long'));
      });
      this.queue = this.queue.filter(item => !stuckItems.includes(item));
    }
  }

  /**
   * Force recovery when queue appears stuck
   */
  forceRecovery() {
    console.log('🔄 Forcing queue recovery...');
    this.processing = false;
    this.currentItem = null;
    
    // Clear any existing timeouts
    if (this.recoveryTimeout) {
      clearTimeout(this.recoveryTimeout);
    }
    
    // Restart processing after a short delay
    this.recoveryTimeout = setTimeout(() => {
      if (this.queue.length > 0) {
        console.log('🔄 Restarting queue processing after recovery');
        this.processNext();
      }
    }, 5000);
  }
}

// Create singleton instance
const processingQueue = new ProcessingQueue();

// Auto-start queue recovery (only in browser)
if (typeof window !== 'undefined') {
  import('./queueRecovery').then(({ default: queueRecovery }) => {
    queueRecovery.start();
  });
}

export default processingQueue;
